import {
  type TStructuredLogResponder,
  type TFieldBasedValidationResult,
  type TFieldBasedValidators,
  type IStructuredLogValidationResult,
} from '../types/structured-log-types';

/**
 * `validateRequiredFields` checks if the required fields are present in the object data.
 * @param {Record<string, any>} data - object to validate
 * @param {Array<string>} requiredFields - array of required fields to check
 * @returns {object} validation result with isValid and errorMessage, if any required fields are missing
 * @template T - type of the object to validate
 */
export const validateRequiredFields = <T extends Record<string, any>>(
  data: T,
  requiredFields: Array<keyof T>
): { isValid: true } | { isValid: false; errorMessage: string; } => {
  const missingFields = requiredFields.reduce<typeof requiredFields>((acc, key) => {
    if (!data[key]) {
      acc.push(key);
    }
    return acc;
  }, []);

  if (missingFields.length) {
    return {
      isValid: false,
      errorMessage: `Missing required fields: ${missingFields.join(', ')}`,
    };
  }

  return { isValid: true };
};

/**
 * `validateFields` validates each field in the object data using the provided validators.
 * @param {Record<string, any>} data - object to validate
 * @param {object} validators - validators for each field in the object
 * @returns {object} validation result with valid values and error messages
 * @template T - type of the object to validate
 */
export const validateFields = <T extends Record<string, any>>(
  data: T,
  validators: TFieldBasedValidators<T>
): IStructuredLogValidationResult<T> => {
  const validValues: Partial<T> = {};
  const errorMessages: string[] = [];

  Object.entries(data).forEach(([key, value]) => {
    const validator = validators[key as keyof T] as (value: any) => TFieldBasedValidationResult;
    if (!validator) {
      // if validator not present for the key, the value will be considered valid
      validValues[key as keyof T] = value;
    } else {
      const result = validator(value);
      if (result.isValid === true && result.isSensitive !== true) {
        validValues[key as keyof T] = result.validValue;
      }
      if (result.isValid === false) {
        errorMessages.push(result.errorMessage);
      }
    }
  });

  return {
    isValid: !errorMessages.length,
    validValues: validValues as T,
    errorMessages,
  };
};

const validateConversationId = (conversationId: string): TFieldBasedValidationResult => {
  if (
    conversationId != null
    && typeof conversationId !== 'string'
  ) {
    return {
      isValid: false,
      errorMessage: 'conversationId should be string',
    };
  }
  return { isValid: true, validValue: conversationId };
};

const validateTimestamp = (timestamp: string): TFieldBasedValidationResult => {
  if (
    timestamp != null
    && (
      typeof timestamp !== 'string'
      || Number.isNaN(Date.parse(timestamp))
    )
  ) {
    return {
      isValid: false,
      errorMessage: 'timestamp should be a valid date string in UTC format',
    };
  }
  return { isValid: true, validValue: timestamp };
};

const validateUid = (uid: string): TFieldBasedValidationResult => {
  if (
    uid != null
    && typeof uid !== 'string'
  ) {
    return {
      isValid: false,
      errorMessage: 'uid should be string',
    };
  }
  return { isValid: true, validValue: uid };
};

const validateCid = (cid: string): TFieldBasedValidationResult => {
  if (
    cid != null
    && typeof cid !== 'string'
  ) {
    return {
      isValid: false,
      errorMessage: 'cid should be string',
    };
  }
  return { isValid: true, validValue: cid };
};

const validateAid = (aid: string): TFieldBasedValidationResult => {
  if (
    aid != null
    && typeof aid !== 'string'
  ) {
    return {
      isValid: false,
      errorMessage: 'aid should be string',
    };
  }
  return { isValid: true, validValue: aid };
};

const validateProduct = (product: string): TFieldBasedValidationResult => {
  if (
    product != null
    && typeof product !== 'string'
  ) {
    return {
      isValid: false,
      errorMessage: 'product should be string',
    };
  }
  return { isValid: true, validValue: product };
};

const validateResponder = (responder: TStructuredLogResponder): TFieldBasedValidationResult => {
  const validResponders: TStructuredLogResponder[] = ['Copilot', 'User', 'UserWithAgent', 'Agent'];
  if (
    responder != null
    && !validResponders.includes(responder)
  ) {
    return {
      isValid: false,
      errorMessage: `responder should be one of: ${validResponders.join(', ')}`,
    };
  }
  return { isValid: true, validValue: responder };
};

const validateActivityType = (activityType: string): TFieldBasedValidationResult => {
  if (
    activityType != null
    && typeof activityType !== 'string'
  ) {
    return {
      isValid: false,
      errorMessage: 'activityType should be string',
    };
  }
  return { isValid: true, validValue: activityType };
};

const validateActivityText = (activityText: string): TFieldBasedValidationResult => {
  if (
    activityText != null
    && typeof activityText !== 'string'
  ) {
    return {
      isValid: false,
      errorMessage: 'activityText should be string',
    };
  }
  return { isValid: true, validValue: activityText };
};

const validateActivityUsername = (activityUsername: string): TFieldBasedValidationResult => {
  if (
    activityUsername != null
    && typeof activityUsername !== 'string'
  ) {
    return {
      isValid: false,
      errorMessage: 'activityUsername should be string, if present',
    };
  }
  return { isValid: true, validValue: activityUsername, isSensitive: true };
};

const validateLocale = (locale: string): TFieldBasedValidationResult => {
  if (
    locale != null
    && typeof locale !== 'string'
  ) {
    return {
      isValid: false,
      errorMessage: 'locale should be string',
    };
  }
  return { isValid: true, validValue: locale };
};

const validateAdditionalContext = (additionalContext?: Record<string, any>): TFieldBasedValidationResult => {
  if (
    additionalContext != null
    && (
      typeof additionalContext !== 'object'
      || Array.isArray(additionalContext)
    )
  ) {
    return {
      isValid: false,
      errorMessage: 'additionalContext should be an object, if present',
    };
  }
  return { isValid: true, validValue: additionalContext };
};

const validateActivityId = (activityId?: string): TFieldBasedValidationResult => {
  if (
    activityId != null
    && typeof activityId !== 'string'
  ) {
    return {
      isValid: false,
      errorMessage: 'activityId should be string, if present',
    };
  }
  return { isValid: true, validValue: activityId };
};

const validateReplyToId = (replyToId?: string): TFieldBasedValidationResult => {
  if (
    replyToId != null
    && typeof replyToId !== 'string'
  ) {
    return {
      isValid: false,
      errorMessage: 'replyToId should be string, if present',
    };
  }
  return { isValid: true, validValue: replyToId };
};

const validateSiteLinks = (siteLinks?: Record<string, string>[]): TFieldBasedValidationResult => {
  if (
    siteLinks != null
    && (
      !Array.isArray(siteLinks)
      || siteLinks.some(link => typeof link !== 'object' || Array.isArray(link))
    )
  ) {
    return {
      isValid: false,
      errorMessage: 'siteLinks should be an array of objects, if present',
    };
  }
  return { isValid: true, validValue: siteLinks };
};

const validateLearnMoreLinks = (learnMoreLinks?: Record<string, string>[]): TFieldBasedValidationResult => {
  if (
    learnMoreLinks != null
    && (!Array.isArray(learnMoreLinks) || learnMoreLinks.some(link => typeof link !== 'object' || Array.isArray(link)))
  ) {
    return {
      isValid: false,
      errorMessage: 'learnMoreLinks should be an array of objects, if present',
    };
  }
  return { isValid: true, validValue: learnMoreLinks };
};

const validateQuestionSuggestions = (questionSuggestions?: Record<string, string>[]): TFieldBasedValidationResult => {
  if (
    questionSuggestions != null
    && (!Array.isArray(questionSuggestions) || questionSuggestions.some(link => typeof link !== 'object' || Array.isArray(link)))
  ) {
    return {
      isValid: false,
      errorMessage: 'questionSuggestions should be an array of objects, if present',
    };
  }
  return { isValid: true, validValue: questionSuggestions };
};

const structuredLogValidators = {
  conversationId: validateConversationId,
  timestamp: validateTimestamp,
  uid: validateUid,
  cid: validateCid,
  aid: validateAid,
  product: validateProduct,
  responder: validateResponder,
  activityType: validateActivityType,
  activityText: validateActivityText,
  activityUsername: validateActivityUsername,
  locale: validateLocale,
  additionalContext: validateAdditionalContext,
  activityId: validateActivityId,
  replyToId: validateReplyToId,
  siteLinks: validateSiteLinks,
  learnMoreLinks: validateLearnMoreLinks,
  questionSuggestions: validateQuestionSuggestions,
};

const requiredStructuredLogFields = [
  'conversationId',
  'timestamp',
  'uid',
  'cid',
  'aid',
  'product',
  'responder',
  'activityType',
  'activityText',
  'locale',
];

/**
 * `validateStructuredLog` validates a structured log object by checking for required fields and validating each field using the provided validators.
 * @param {Record<string, any>} structuredLog - structured log to validate
 * @returns {object} validation result with valid values and error messages
 * @template T - type of the structured log object to validate
 */
export const validateStructuredLog = <T extends Record<string, any>>(structuredLog: T): IStructuredLogValidationResult<T> => {
  const requiredFieldsValidationResult = validateRequiredFields(structuredLog, requiredStructuredLogFields);
  const fieldBasedValidationResult = validateFields(structuredLog, structuredLogValidators as unknown as TFieldBasedValidators<T>);

  const isValid = requiredFieldsValidationResult.isValid && fieldBasedValidationResult.isValid;

  const errorMessages = requiredFieldsValidationResult.isValid === false
    ? [requiredFieldsValidationResult.errorMessage].concat(fieldBasedValidationResult.errorMessages)
    : fieldBasedValidationResult.errorMessages;

  return {
    isValid,
    validValues: fieldBasedValidationResult.validValues,
    errorMessages,
  };
};
